import { MouseEvent, useEffect, useRef, useState } from 'react';
import {
    Outlet,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import { styled as MuiStyle } from '@mui/material/styles';
import {
    CssBaseline,
} from '@mui/material';

import {
    RootLayoutContainer,
} from '../../Styles/PageLayout/rootLayoutStyles';

import { Authentication } from "../../Models/Authentication/Authentication";

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import SettingsHelper from '../../Utilities/Helpers/SettingsHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { CachingEnum } from "../../Utilities/Enums/CachingEnum";
import { 
    AuthenticationField,
} from "../../Utilities/Enums/ParametersEnum";
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CurrencyEnum } from '../../Utilities/Enums/CurrencyEnums';

import { AssetImages } from '../../Constants/Assets';
import { GlobalConstants } from '../../Constants/GlobalConstants';

import ErrorPage from '../../CustomViews/Pages/ErrorPages/ErrorPage';
import WelcomePage from '../Dashboard/WelcomePage';
import Footer from './Footer/Footer';
import SideMenu from './Navigations/SideMenu';
import HeaderNavigation from './Navigations/HeaderNavigation';

const Main = MuiStyle('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    padding: '0px',

    flexGrow: 1,
    // padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${(GlobalConstants?.DRAWER_WIDTH ?? 240)}px`,
    backgroundColor: "pageBackground",
    // height: '100vh',
    height: '-webkit-fill-available',
    variants: [
        {
            props: ({ open }) => open,
            style: {
                transition: theme.transitions.create('margin', {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                marginLeft: 0,
            },
        },
    ],
}));

const DrawerHeader = MuiStyle('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

const RootLayout = ({
    isError = false,
}) => {

    //Variables
    type Anchor = 'top' | 'left' | 'bottom' | 'right';
    const location = useLocation();
    const navigate = useNavigate();
    const ref: any = useRef(null);
    const expiredTokenBroadcast = new BroadcastChannel('online-session');
    const isDarkMode = LocalStorageHelper.isDarkMode();
    let userData: Authentication = LocalStorageHelper.getUserData();
    const byPassLogIn = GlobalFunctionHelper.getBypassLogin();

    let queryParams = new URLSearchParams(location.search);
    let pageId = queryParams.get('id') != null && queryParams.get('id') != "" ? queryParams.get('id') : null;
    let routePath = location?.pathname ?? null;
    let vType = parseInt(queryParams.get('view') ?? ViewType.PREVIEW.toString());

    // Use states
    const [pos, setPos] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(true);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

    //Variables
    let isPathValid = AccessibilityHelper.validatePath(pageId, routePath, vType);

    //Event Handlers
    const handleTop = (e: any) => {
        ref.current.scrollTo({ top: 0, behavior: 'smooth' });
        setPos(false);
    };

    const handleScroll = () => {
        if (ref.current.scrollTop > 50) {
            if (!pos) {
                setPos(true);
            }
        } else {
            if (pos) {
                setPos(false);
            }
        }
    };

    const handleOpenUserMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    useEffect(() => {
        SettingsHelper.UpdateWebPageDetails();
        const temp = ref.current;
        if (temp != undefined) {
            temp.addEventListener("scroll", handleScroll);
            return () => temp.removeEventListener("scroll", handleScroll);
        }
    });

    useEffect(() => {
        expiredTokenBroadcast.onmessage = (event) => {
            if (event.data?.type === 'EXPIRED_SESSION') {
                navigate(RouteEnum.LOGIN, {replace: true});
            }
        };
    }, [])


    if(userData?.[AuthenticationField.MUST_CHANGE_PASSWORD] && routePath != RouteEnum.RESET_PASSWORD) {
        navigate(RouteEnum.RESET_PASSWORD, {replace: true});
    }

    //Rendering Functions
    const renderHeaderNavigation = () => {
        return <HeaderNavigation
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer}
            anchorElUser={anchorElUser}
            setAnchorElUser={setAnchorElUser}
            handleOpenUserMenu={handleOpenUserMenu} />
    }

    const renderSideMenu = () => {
        return <SideMenu
            openDrawer={openDrawer}
            setOpenDrawer={setOpenDrawer} />
    }

    const renderRootLayout = () => {
        return <RootLayoutContainer $darkMode={isDarkMode}>
            <CssBaseline />

            {renderSideMenu()}
            {renderHeaderNavigation()}
            
            <Main open={openDrawer} className='mainDiv' sx={{backgroundColor: 'pageBackground'}}>
                {/*<DrawerHeader />*/}
                <div className="pageContent">
                    <div className='scrollableContent'>
                        {(isError || !isPathValid) ? (routePath == RouteEnum.DASHBOARD ? <WelcomePage /> : <ErrorPage />) : <Outlet />}
                    </div>
                </div>
                <Footer />
            </Main>
        </RootLayoutContainer>
    }

    return byPassLogIn ? <Outlet /> : renderRootLayout()
}


export default RootLayout;